import { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Camera } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";
import { api, imgUrl, errMsg } from "@/lib/api";
import { ContactFields, EMPTY_CUSTOMER, contactValid, consentMissing, inputCls } from "@/components/shop/ContactFields";
import { InquirySent } from "@/components/shop/InquirySent";

const Photos = ({ photos, setPhotos }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    try {
      const out = [];
      for (const f of files.slice(0, 5 - photos.length)) {
        const fd = new FormData();
        fd.append("file", f);
        out.push((await api.post("/inquiries/upload", fd)).data.path);
      }
      setPhotos([...photos, ...out]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-3">
      {photos.map((p, i) => (
        <div key={p} className="relative w-28 h-28 border border-[#3B4157]">
          <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
          <button type="button" data-testid={`finder-remove-photo-${i}`} onClick={() => setPhotos(photos.filter((x) => x !== p))} className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center bg-red-500 text-white"><X className="w-3 h-3" /></button>
        </div>
      ))}
      {photos.length < 5 && (
        <label className="w-28 h-28 border border-dashed border-slate-600 grid place-items-center text-center cursor-pointer hover:border-orange-500 text-slate-400 hover:text-orange-500 transition-colors">
          <span className="text-xs space-y-1">{busy ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : <Upload className="w-6 h-6 mx-auto" />}<span className="block">{t("upload_photo")}</span></span>
          <input data-testid="finder-photo-upload" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => upload([...e.target.files])} />
        </label>
      )}
    </div>
  );
};

export default function SeatFinder() {
  const { t } = useLang();
  useSeo({ title: t("finder_title"), description: t("finder_cta_sub") });
  const [photos, setPhotos] = useState([]);
  const [machine, setMachine] = useState("");
  const [message, setMessage] = useState("");
  const [c, setC] = useState(EMPTY_CUSTOMER);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!photos.length && !machine.trim()) return toast.error(t("finder_need_photo"));
    if (!contactValid(c)) return toast.error(t("fill_all"));
    if (consentMissing(c, t, toast)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inquiries", { type: "selection", photos, machine, message, customer: c });
      setSent(data);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  if (sent) return <InquirySent number={sent.number} />;

  return (
    <div data-testid="seat-finder-page" className="mx-auto max-w-4xl px-4 sm:px-6 py-12">
      <div className="text-xs font-mono-x uppercase tracking-[0.25em] text-orange-500">// {t("finder_kicker")}</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-4">{t("finder_title")}</h1>
      <p className="text-slate-400 mt-4 max-w-2xl">{t("finder_sub")}</p>
      <div className="mt-8 border border-orange-500/30 bg-orange-500/5 p-5 flex gap-4">
        <Camera className="w-6 h-6 text-orange-500 shrink-0" />
        <p className="text-sm text-slate-300">{t("finder_howto")}</p>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-8">
        <section className="border border-[#3B4157] p-6">
          <h2 className="font-display font-bold uppercase text-xl"><span className="text-orange-500 font-mono-x mr-2">01</span>{t("finder_photos")}</h2>
          <div className="mt-5"><Photos photos={photos} setPhotos={setPhotos} /></div>
          <div className="grid gap-4 mt-6">
            <label className="text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>{t("finder_machine")}</span>
              <input data-testid="finder-machine-input" className={inputCls} value={machine} onChange={(e) => setMachine(e.target.value)} placeholder="John Deere 6155R, 2018" /></label>
            <label className="text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>{t("finder_message")}</span>
              <textarea data-testid="finder-message-input" rows={3} className={`${inputCls} h-auto py-2`} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
          </div>
        </section>
        <section className="border border-[#3B4157] p-6">
          <h2 className="font-display font-bold uppercase text-xl"><span className="text-orange-500 font-mono-x mr-2">02</span>{t("contacts")}</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-6"><ContactFields c={c} setC={setC} prefix="finder" showNotes={false} /></div>
        </section>
        <button data-testid="finder-submit-btn" disabled={busy} className="w-full h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("send_inquiry")}
        </button>
      </form>
    </div>
  );
}
